import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import ContactSection from '../components/ContactSection';
import PrismaticBurst from '../components/PrismaticBurst';
import SplitText from '../components/SplitText';

// ── Work proof images (landscape, from Unsplash free)
const WORK_IMAGES = {
  'web-design': [
    { src: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80', alt: 'Web design project', size: 'tall' },
    { src: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&q=80', alt: 'Website mockup', size: 'wide' },
    { src: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=800&q=80', alt: 'Web development', size: 'normal' },
    { src: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&q=80', alt: 'UI design', size: 'tall' },
    { src: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80', alt: 'Laptop design', size: 'wide' },
    { src: 'https://images.unsplash.com/photo-1534670007418-fbb7f6cf32c3?w=800&q=80', alt: 'Design tools', size: 'normal' },
  ],
  'branding': [
    { src: 'https://images.unsplash.com/photo-1572044162444-ad60f128bdea?w=800&q=80', alt: 'Brand identity' },
    { src: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80', alt: 'Logo design' },
    { src: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=800&q=80', alt: 'Branding collateral' },
    { src: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&q=80', alt: 'Brand guide' },
    { src: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80', alt: 'Brand strategy' },
    { src: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=800&q=80', alt: 'Typography' },
    { src: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800&q=80', alt: 'Brand mockup' },
    { src: 'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=800&q=80', alt: 'Packaging design' },
    { src: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?w=800&q=80', alt: 'Brand presentation' },
    { src: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=800&q=80', alt: 'Creative workspace' },
    { src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80', alt: 'Brand photography' },
    { src: 'https://images.unsplash.com/photo-1536329583941-14287ec6fc4e?w=800&q=80', alt: 'Visual identity' },
    { src: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=800&q=80', alt: 'Logo process' },
    { src: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=800&q=80', alt: 'Design system' },
    { src: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?w=800&q=80', alt: 'Creative team' },
    { src: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=800&q=80', alt: 'Brand stationery' },
    { src: 'https://images.unsplash.com/photo-1471897488648-5eae4ac6686b?w=800&q=80', alt: 'Brand lifestyle' },
    { src: 'https://images.unsplash.com/photo-1600132806370-bf17e65e942f?w=800&q=80', alt: 'Brand identity 2' },
    { src: 'https://images.unsplash.com/photo-1634942537034-2531766767d1?w=800&q=80', alt: 'Color palette' },
    { src: 'https://images.unsplash.com/photo-1613909207039-6b173b755cc1?w=800&q=80', alt: 'Brand colors' },
  ],
  'digital-marketing': [
    { src: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80', alt: 'Analytics dashboard' },
    { src: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=800&q=80', alt: 'Social media' },
    { src: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=800&q=80', alt: 'Content strategy' },
    { src: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80', alt: 'Campaign results' },
    { src: 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=800&q=80', alt: 'Social content' },
    { src: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80', alt: 'Marketing team' },
    { src: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&q=80', alt: 'Digital strategy' },
    { src: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80', alt: 'Content creation' },
    { src: 'https://images.unsplash.com/photo-1512756290469-ec264b7fbf87?w=800&q=80', alt: 'SEO analytics' },
    { src: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80', alt: 'Email marketing' },
    { src: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80', alt: 'Performance graph' },
    { src: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80', alt: 'Digital workspace' },
    { src: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', alt: 'Data insights' },
    { src: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80', alt: 'Marketing strategy' },
    { src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80', alt: 'Team collaboration' },
    { src: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80', alt: 'Campaign planning' },
    { src: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80', alt: 'Content calendar' },
    { src: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80', alt: 'Brand campaign' },
    { src: 'https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?w=800&q=80', alt: 'Social ads' },
    { src: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=800&q=80', alt: 'Growth metrics' },
  ],
  'advertising': [
    { src: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80', alt: 'Ad campaign' },
    { src: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80', alt: 'Performance metrics' },
    { src: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&q=80', alt: 'Campaign analytics' },
    { src: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', alt: 'ROI growth' },
  ],
};

const SERVICE_DATA = {
  'web-design': {
    title: 'Web Design', tagline: 'Websites that work as hard as you do.',
    description: 'We craft beautiful, high-performance websites that turn visitors into customers. Every pixel is intentional, every interaction is considered.',
    color: '#4a90d9', burstColors: ['#4a90d9', '#a8d4ff', '#0a1a2e'],
    features: [
      { title: 'Custom UI/UX Design', desc: 'Tailor-made interfaces designed around your users and brand identity.', size: 'large' },
      { title: 'Responsive Development', desc: 'Pixel-perfect on every screen — desktop, tablet, and mobile.', size: 'small' },
      { title: 'Performance Optimized', desc: 'Fast load times, clean code, and SEO-ready structure.', size: 'small' },
      { title: 'CMS Integration', desc: 'Easy-to-manage content systems — WordPress, Webflow, and more.', size: 'medium' },
      { title: 'E-Commerce Ready', desc: 'Seamless shopping experiences built to convert.', size: 'medium' },
      { title: 'Ongoing Support', desc: "We don't just build and leave — we're with you for the long run.", size: 'small' },
    ],
    process: ['Discovery', 'Wireframe', 'Design', 'Development', 'Launch', 'Support'],
    workLayout: 'landscape-stack',
    title: 'Branding', tagline: 'Your identity, crafted with intention.',
    description: "A brand is more than a logo — it's your story, your voice, your promise. We build complete brand systems that resonate and endure.",
    color: '#c8602a', burstColors: ['#c8602a', '#ffb07a', '#1a0800'],
    features: [
      { title: 'Brand Strategy', desc: 'Deep-dive into your mission, vision, and positioning.', size: 'large' },
      { title: 'Logo & Identity', desc: 'Timeless logos and complete visual identity systems.', size: 'medium' },
      { title: 'Brand Guidelines', desc: 'Comprehensive guides that keep your brand consistent everywhere.', size: 'medium' },
      { title: 'Typography & Color', desc: 'Curated palettes and typefaces that tell your story.', size: 'small' },
      { title: 'Brand Voice', desc: 'Messaging frameworks that define how you speak and connect.', size: 'small' },
      { title: 'Collateral Design', desc: 'Business cards, letterheads, and all brand touchpoints.', size: 'small' },
    ],
    process: ['Research', 'Strategy', 'Concept', 'Refine', 'Deliver', 'Guide'],
    workLayout: 'pinterest',
  },
  'digital-marketing': {
    title: 'Digital Marketing', tagline: "Growth that's measurable, not guesswork.",
    description: "Data-driven strategies across every digital channel. We don't just run campaigns — we build systems that consistently deliver ROI.",
    color: '#2a9d6a', burstColors: ['#2a9d6a', '#7affc0', '#041208'],
    features: [
      { title: 'Social Media Management', desc: 'Consistent, engaging content that builds your community.', size: 'large' },
      { title: 'Content Strategy', desc: 'Stories that attract, engage, and convert your target audience.', size: 'small' },
      { title: 'SEO & SEM', desc: 'Visibility where it matters — when people are searching for you.', size: 'small' },
      { title: 'Email Marketing', desc: 'Automated sequences and campaigns that nurture leads.', size: 'medium' },
      { title: 'Analytics & Reporting', desc: "Clear dashboards so you always know what's working.", size: 'medium' },
      { title: 'Campaign Management', desc: 'End-to-end management of your digital campaigns.', size: 'small' },
    ],
    process: ['Audit', 'Strategy', 'Create', 'Publish', 'Analyze', 'Optimize'],
    workLayout: 'pinterest',
  },
  'advertising': {
    title: 'Advertising', tagline: 'Ads that stop the scroll.',
    description: 'We create paid advertising campaigns across Meta, Google, TikTok, and beyond that capture attention and drive real results for your business.',
    color: '#b03070', burstColors: ['#b03070', '#ff7ab8', '#1a0210'],
    features: [
      { title: 'Meta Ads', desc: 'Facebook and Instagram campaigns engineered for maximum reach and conversions.', size: 'large' },
      { title: 'Google Ads', desc: 'Search, display, and shopping campaigns targeting buyers with intent.', size: 'medium' },
      { title: 'TikTok Ads', desc: 'Native-feel content ads built for Gen Z and millennial audiences.', size: 'medium' },
      { title: 'Creative Production', desc: 'Scroll-stopping visuals, videos, and copy that demand attention.', size: 'small' },
      { title: 'Retargeting', desc: 'Bring back warm audiences and close more conversions.', size: 'small' },
      { title: 'A/B Testing', desc: 'Continuous testing to improve performance and lower your CPC.', size: 'small' },
    ],
    process: ['Brief', 'Research', 'Creative', 'Launch', 'Monitor', 'Scale'],
    workLayout: 'landscape-stack',
  },
};

// ── Bento card (desktop: grid, mobile: single col)
function BentoCard({ feat, color, index }) {
  const [hovered, setHovered] = useState(false);
  const SIZE_DESKTOP = {
    large:  { gridColumn: 'span 2', gridRow: 'span 2', minHeight: '240px' },
    medium: { gridColumn: 'span 1', gridRow: 'span 2', minHeight: '200px' },
    small:  { gridColumn: 'span 1', gridRow: 'span 1', minHeight: '140px' },
  };
  const desktopSize = SIZE_DESKTOP[feat.size] || SIZE_DESKTOP.small;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94, y: 20 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, duration: 0.55, ease: [0.22,1,0.36,1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="bento-card"
      style={{
        ...desktopSize,
        background: hovered ? color + '14' : 'rgba(255,255,255,0.025)',
        border: '1px solid ' + (hovered ? color + '55' : 'rgba(255,255,255,0.07)'),
        borderRadius: '20px',
        padding: '28px',
        transition: 'all 0.35s cubic-bezier(0.22,1,0.36,1)',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered ? '0 16px 40px rgba(0,0,0,0.35)' : 'none',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, marginBottom: '16px', opacity: hovered ? 1 : 0.4, transition: 'opacity 0.3s' }} />
      <h4 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 700, fontSize: feat.size === 'large' ? '20px' : '16px', color: '#f0f0f8', marginBottom: '10px' }}>{feat.title}</h4>
      <p style={{ fontSize: '13px', color: 'rgba(240,240,248,0.45)', lineHeight: 1.65 }}>{feat.desc}</p>
    </motion.div>
  );
}

// ── Bento photo grid (web-design)
function BentoWork({ images, color }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {images.map((img, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}>
            <img src={img.src} alt={img.alt} style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }} />
          </motion.div>
        ))}
      </div>
    );
  }

  // Desktop: bento grid with varied sizes
  const layout = [
    { gridColumn: 'span 2', gridRow: 'span 2', height: '420px' }, // big
    { gridColumn: 'span 1', gridRow: 'span 1', height: '200px' }, // normal
    { gridColumn: 'span 1', gridRow: 'span 1', height: '200px' }, // normal
    { gridColumn: 'span 1', gridRow: 'span 2', height: '420px' }, // tall
    { gridColumn: 'span 2', gridRow: 'span 1', height: '200px' }, // wide
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'auto', gap: '12px' }}>
      {images.slice(0, 5).map((img, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ delay: i * 0.09, duration: 0.55, ease: [0.22,1,0.36,1] }}
          style={{ gridColumn: layout[i].gridColumn, gridRow: layout[i].gridRow, borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <img src={img.src} alt={img.alt} style={{ width: '100%', height: layout[i].height, objectFit: 'cover', display: 'block' }} />
        </motion.div>
      ))}
    </div>
  );
}

// ── Pinterest layout (branding, digital-marketing) — 3 col masonry
function PinterestWork({ images, color }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  if (isMobile) {
    const col1 = images.filter((_, i) => i % 2 === 0);
    const col2 = images.filter((_, i) => i % 2 === 1);
    const mHeights = ['180px','240px','200px','280px','160px','220px','260px','190px','230px','200px'];
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {[col1, col2].map((col, ci) => (
          <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {col.map((img, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                style={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}>
                <img src={img.src} alt={img.alt} style={{ width: '100%', height: mHeights[(ci * col.length + i) % mHeights.length], objectFit: 'cover', display: 'block' }} />
              </motion.div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Desktop: 3-column Pinterest masonry
  const col1 = images.filter((_, i) => i % 3 === 0);
  const col2 = images.filter((_, i) => i % 3 === 1);
  const col3 = images.filter((_, i) => i % 3 === 2);
  const heights = ['260px','340px','200px','300px','240px','280px','220px','360px'];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', alignItems: 'start' }}>
      {[col1, col2, col3].map((col, ci) => (
        <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {col.map((img, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: (ci + i * 3) * 0.06, duration: 0.55 }}
              style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <img src={img.src} alt={img.alt}
                style={{ width: '100%', height: heights[(ci * 2 + i) % heights.length], objectFit: 'cover', display: 'block' }} />
            </motion.div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Landscape stack (advertising) — taller cards, no label
function LandscapeWork({ images, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {images.map((img, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ delay: i * 0.1 }}
          style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <img src={img.src} alt={img.alt} style={{ width: '100%', height: '380px', objectFit: 'cover', display: 'block' }} />
        </motion.div>
      ))}
    </div>
  );
}

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.7, delay, ease: [0.22,1,0.36,1] }}
    >
      {children}
    </motion.div>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = SERVICE_DATA[slug];
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [slug]);

  if (!data) return (
    <div style={{ background: '#04080f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f0f0f8' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontFamily: "'Mona Sans',sans-serif", fontSize: '48px', fontWeight: 800, marginBottom: '16px' }}>404</h1>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: '#f0f0f8', padding: '10px 24px', borderRadius: '12px', cursor: 'pointer', fontFamily: "'Mona Sans',sans-serif" }}>Go Home</button>
      </div>
    </div>
  );

  const otherServices = Object.entries(SERVICE_DATA).filter(([k]) => k !== slug);
  const workImages = WORK_IMAGES[slug] || [];

  return (
    <div style={{ background: '#04080f', minHeight: '100vh' }}>
      <Navbar />

      {/* ── Hero ── */}
      <section style={{ position: 'relative', minHeight: isMobile ? '55vh' : '65vh', display: 'flex', alignItems: 'flex-end', padding: isMobile ? '120px 20px 60px' : '140px 48px 80px', overflow: 'hidden' }}>
        <PrismaticBurst colors={data.burstColors} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 160% 50% at 50% 0%, #04080e 0%, transparent 60%)', zIndex: 1 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 30% 100% at 0% 50%, rgba(4,8,14,0.8) 0%, transparent 60%), radial-gradient(ellipse 30% 100% at 100% 50%, rgba(4,8,14,0.8) 0%, transparent 60%)', zIndex: 1 }} />
        <div style={{ position: 'relative', zIndex: 3, maxWidth: '900px' }}>
          <motion.button onClick={() => navigate('/')} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            style={{ background: 'none', border: 'none', color: 'rgba(240,240,248,0.35)', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.15em', cursor: 'pointer', marginBottom: '28px', padding: 0 }}>
            ← BACK_HOME
          </motion.button>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.2em', color: data.color, marginBottom: '12px', textTransform: 'uppercase' }}>
            Our Services
          </motion.p>
          <h1 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 800, fontSize: isMobile ? 'clamp(36px,10vw,56px)' : 'clamp(40px,7vw,80px)', letterSpacing: '-0.025em', color: '#f0f0f8', lineHeight: 1.0, marginBottom: '16px' }}>
            <SplitText text={data.title} delay={0.2} stagger={0.07} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
            style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(240,240,248,0.45)', maxWidth: '500px', lineHeight: 1.7 }}>
            {data.tagline}
          </motion.p>
        </div>
      </section>

      {/* ── Description ── */}
      <section style={{ padding: isMobile ? '48px 20px 0' : '80px 48px 0', maxWidth: '900px', margin: '0 auto' }}>
        <Reveal>
          <p style={{ fontSize: isMobile ? '15px' : '18px', color: 'rgba(240,240,248,0.6)', lineHeight: 1.8, fontWeight: 300 }}>{data.description}</p>
        </Reveal>
      </section>

      {/* ── Process ── */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', maxWidth: '900px', margin: '0 auto' }}>
        <Reveal>
          <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.2em', color: data.color, marginBottom: '20px', textTransform: 'uppercase' }}>Our Process</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {data.process.map((step, i) => (
              <motion.div key={step} initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: data.color, opacity: 0.6 }}>0{i+1}</span>
                <span style={{ fontSize: '13px', color: 'rgba(240,240,248,0.7)', fontWeight: 500 }}>{step}</span>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ── What's Included — Bento (desktop) / Card stack (mobile) ── */}
      <section style={{ padding: isMobile ? '0 20px 60px' : '0 48px 80px', maxWidth: '1100px', margin: '0 auto' }}>
        <Reveal>
          <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.2em', color: data.color, marginBottom: '28px', textTransform: 'uppercase' }}>What's Included</p>
        </Reveal>

        {isMobile ? (
          // Mobile: simple vertical card stack
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {data.features.map((feat, i) => (
              <motion.div key={feat.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '24px 20px' }}
              >
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: data.color, marginBottom: '12px' }} />
                <h4 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 700, fontSize: '16px', color: '#f0f0f8', marginBottom: '8px' }}>{feat.title}</h4>
                <p style={{ fontSize: '13px', color: 'rgba(240,240,248,0.45)', lineHeight: 1.65 }}>{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        ) : (
          // Desktop: magic bento grid
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'minmax(120px, auto)', gap: '12px' }}>
            {data.features.map((feat, i) => <BentoCard key={feat.title} feat={feat} color={data.color} index={i} />)}
          </div>
        )}
      </section>

      {/* ── Our Recent Work ── */}
      <section style={{ padding: isMobile ? '0 20px 60px' : '0 48px 100px', maxWidth: '1100px', margin: '0 auto' }}>
        <Reveal>
          <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.2em', color: data.color, marginBottom: '8px', textTransform: 'uppercase' }}>Our Recent Work</p>
          <h3 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 800, fontSize: isMobile ? '24px' : '32px', color: '#f0f0f8', marginBottom: '32px', letterSpacing: '-0.02em' }}>
            Work proof.
          </h3>
        </Reveal>

        {data.workLayout === 'bento'
          ? <BentoWork images={workImages} color={data.color} />
          : data.workLayout === 'landscape-stack'
          ? <LandscapeWork images={workImages} color={data.color} />
          : <PinterestWork images={workImages} color={data.color} />
        }
      </section>

      {/* ── Other Services ── */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.2em', color: 'rgba(240,240,248,0.25)', marginBottom: '20px', textTransform: 'uppercase' }}>Other Services</p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px,1fr))', gap: '10px' }}>
            {otherServices.map(([k, s], i) => (
              <motion.div key={k}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                onClick={() => navigate('/services/' + k)}
                whileHover={{ background: 'rgba(255,255,255,0.05)', y: -3 }}
                style={{ padding: '20px', cursor: 'pointer', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', transition: 'all 0.25s ease' }}
              >
                <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'rgba(240,240,248,0.3)', letterSpacing: '0.1em', marginBottom: '8px', textTransform: 'uppercase' }}>Service</p>
                <h4 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 700, fontSize: '15px', color: '#f0f0f8' }}>{s.title}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />

      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: isMobile ? '24px 20px' : '32px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1200px', margin: '0 auto', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ fontSize: '11px', color: 'rgba(240,240,248,0.25)', letterSpacing: '0.05em' }}>© 2026 DOIT Creative. All rights reserved.</p>
        <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'rgba(240,240,248,0.15)', letterSpacing: '0.15em' }}>DOIT_CREATIVE / EST.2020</p>
      </footer>
    </div>
  );
}
